import React, { useState, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { trackEvent } from 'utils/analytics'
import { SocialIcon } from './socialLinkIcons'
import { buildRenderableLinks } from 'utils/socialLinks'

// Solid background colors for the round buttons (white icon on top)
const BUTTON_COLORS = {
  phone: '#34D399',
  line: '#06C755',
  instagram: '#E1306C',
  email: '#3366FF',
  facebook: '#1877F2',
  threads: '#000000',
  youtube: '#FF0000',
  tiktok: '#000000',
  x: '#000000',
  website: '#3B82F6',
  custom: '#6B7280',
}

// Check if viewport is mobile (<1024px)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

export default function FloatingContactButton({ contact, colors, teamSlug, translations }) {
  const [expanded, setExpanded] = useState(false)
  const isMobile = useIsMobile()
  // Shared conversion + URL building: legacy format handled, hrefs scheme-whitelisted
  const links = useMemo(() => buildRenderableLinks(contact), [contact])

  if (links.length === 0 || !isMobile) return null

  const containerStyle = {
    position: 'fixed',
    right: '12px',
    top: '55%',
    transform: 'translateY(-50%)',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  }

  const buttonStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    backgroundColor: expanded ? '#9CA3AF' : (colors.primary || '#3366FF'),
  }

  const linkStyle = (type) => ({
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    backgroundColor: BUTTON_COLORS[type] || colors.primary || '#3366FF',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    textDecoration: 'none',
  })

  return createPortal(
    <div style={containerStyle}>
      {/* Toggle button */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={buttonStyle}
        aria-label={expanded ? (translations?.close_menu || 'Close contact menu') : (translations?.open_menu || 'Open contact menu')}
        aria-expanded={expanded}
      >
        {expanded ? (
          <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Expanded contact icons */}
      {expanded && links.map((link, idx) => (
        <a
          key={idx}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle(link.type)}
          aria-label={link.display || link.type}
          onClick={() => trackEvent('floating_contact_click', { contact_type: link.type, team_slug: teamSlug })}
        >
          <SocialIcon type={link.type} className="w-5 h-5" />
        </a>
      ))}
    </div>,
    document.body
  )
}
